import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import {
   AreaChart,
   Area,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
} from 'recharts';
import { colors } from '@/constants/colors';
import { currencyFormat } from '@/utils/currencyFormatter';
import DateRangePicker from '@/components/Admin/UI/DateRangePicker';
import { getMyVendoRevenueTimeseriesApi } from '@/api/vendo';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';

/**
 * Revenue wave chart — daily coin + online (e-wallet) revenue with range totals and
 * a date filter. One component so the operator dashboard and the client
 * portal render the exact same chart (the endpoint scopes the data to
 * the caller: owner = all devices, client login = their one device).
 */
/** The dropdown's rows: every main, each followed by its sub-vendos ("└"),
 *  so a shop reads as a group. A sub whose main is not in the list stands
 *  alone. Empty when there is nothing to choose between (one vendo, or the
 *  client portal, which charts its one device). */
export const vendoChartOptions = (
   devices: { _id: string; name: string; parent?: string | null }[]
): { value: string; label: string }[] => {
   const mains = devices.filter((d) => !d.parent);
   const subsOf = (id: string) => devices.filter((d) => d.parent === id);
   const listed = new Set<string>();
   const out: { value: string; label: string }[] = [];
   for (const m of mains) {
      out.push({ value: m._id, label: m.name });
      listed.add(m._id);
      for (const sub of subsOf(m._id)) {
         out.push({ value: sub._id, label: `└ ${sub.name}` });
         listed.add(sub._id);
      }
   }
   for (const d of devices) if (!listed.has(d._id)) out.push({ value: d._id, label: d.name });
   return out;
};

const RevenueAreaChart: React.FC<{
   title?: string;
   height?: number;
   /** Set by a page that shares ONE vendo choice with its other figures (the
    *  dashboard's stat cards). Left out, the chart keeps its own choice. */
   deviceId?: string;
   onDeviceChange?: (id: string) => void;
}> = ({
   title = 'Revenue — coins, online payments & vouchers redeemed',
   height = 240,
   deviceId: controlledId,
   onDeviceChange,
}) => {
   const [range, setRange] = useState({ dateFrom: '', dateTo: '' });
   // One vendo, or the whole account. Ray 2026-09-29: "individual sales
   // monitoring" — the chart only ever summed every vendo together.
   const [ownId, setOwnId] = useState('');
   const deviceId = controlledId ?? ownId;
   const setDeviceId = (id: string) => (onDeviceChange ? onDeviceChange(id) : setOwnId(id));
   const devices = useVendoDevicesStore((st) => st.devices);
   const vendoOptions = useMemo(() => vendoChartOptions(devices), [devices]);
   const { data: revenueSeries } = useQuery({
      queryKey: ['vendo', 'revenue-timeseries', range.dateFrom, range.dateTo, deviceId],
      queryFn: () =>
         getMyVendoRevenueTimeseriesApi(
            range.dateFrom || undefined,
            range.dateTo || undefined,
            deviceId || undefined
         ),
   });
   const chartData = useMemo(
      () =>
         (revenueSeries?.series ?? []).map((p) => ({
            label: new Date(p.date + 'T00:00:00').toLocaleDateString('en-PH', {
               month: 'short',
               day: 'numeric',
            }),
            coin: p.coinRevenue,
            gcash: p.gcashRevenue,
            voucher: p.voucherRevenue ?? 0,
         })),
      [revenueSeries]
   );
   const totals = useMemo(() => {
      const s = revenueSeries?.series ?? [];
      return {
         coin: s.reduce((a, p) => a + p.coinRevenue, 0),
         gcash: s.reduce((a, p) => a + p.gcashRevenue, 0),
         voucher: s.reduce((a, p) => a + (p.voucherRevenue ?? 0), 0),
      };
   }, [revenueSeries]);

   return (
      <RevenueBlock>
         <RevenueHeader>
            <RevenueTitle>{title}</RevenueTitle>
            <RevenueMeta>
               <RevenueTotals>
                  <RevTotal $c={colors.PRIMARY}>
                     Coins {currencyFormat(totals.coin, false)}
                  </RevTotal>
                  <RevTotal $c={colors.SUCCESS}>
                     Online payment {currencyFormat(totals.gcash, false)}
                  </RevTotal>
                  <RevTotal $c={colors.PINK}>
                     Voucher redeemed {currencyFormat(totals.voucher, false)}
                  </RevTotal>
               </RevenueTotals>
               {vendoOptions.length > 1 && (
                  <VendoSelect
                     value={deviceId}
                     onChange={(e) => setDeviceId(e.target.value)}
                     aria-label="Which vendo"
                     title="Show one vendo, or all of them"
                  >
                     <option value="">All vendos</option>
                     {vendoOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                           {o.label}
                        </option>
                     ))}
                  </VendoSelect>
               )}
               <DateRangePicker
                  dateFrom={range.dateFrom}
                  dateTo={range.dateTo}
                  onChange={(from, to) => setRange({ dateFrom: from, dateTo: to })}
               />
            </RevenueMeta>
         </RevenueHeader>
         <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={chartData} margin={{ top: 5, right: 15, left: 0, bottom: 0 }}>
               <defs>
                  <linearGradient id="vendoCoinGrad" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="0%" stopColor={colors.PRIMARY} stopOpacity={0.28} />
                     <stop offset="95%" stopColor={colors.PRIMARY} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="vendoGcashGrad" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="0%" stopColor={colors.SUCCESS} stopOpacity={0.28} />
                     <stop offset="95%" stopColor={colors.SUCCESS} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="vendoVoucherGrad" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="0%" stopColor={colors.PINK} stopOpacity={0.28} />
                     <stop offset="95%" stopColor={colors.PINK} stopOpacity={0} />
                  </linearGradient>
               </defs>
               <CartesianGrid strokeDasharray="3 3" stroke={colors.GRAY13} vertical={false} />
               <XAxis dataKey="label" tick={{ fontSize: 11, fill: colors.GRAY5 }} axisLine={false} tickLine={false} />
               <YAxis tick={{ fontSize: 11, fill: colors.GRAY5 }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `₱${v}`} />
               <Tooltip
                  contentStyle={{ borderRadius: 8, border: `1px solid ${colors.GRAY13}`, fontSize: 12 }}
                  formatter={(value: any, name: any) => [
                     currencyFormat(Number(value), false),
                     name === 'coin' ? 'Coins' : name === 'voucher' ? 'Voucher redeemed' : 'Online payment',
                  ]}
               />
               <Area type="monotone" dataKey="coin" stroke={colors.PRIMARY} strokeWidth={2} fill="url(#vendoCoinGrad)" />
               <Area type="monotone" dataKey="gcash" stroke={colors.SUCCESS} strokeWidth={2} fill="url(#vendoGcashGrad)" />
               <Area type="monotone" dataKey="voucher" stroke={colors.PINK} strokeWidth={2} fill="url(#vendoVoucherGrad)" />
            </AreaChart>
         </ResponsiveContainer>
      </RevenueBlock>
   );
};

export default RevenueAreaChart;

const VendoSelect = styled.select`
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
   border: 1px solid ${colors.GRAY12};
   padding: 7px 28px 7px 10px;
   border-radius: 8px;
   font-size: 13px;
   font-weight: 500;
   max-width: 220px;
   cursor: pointer;
`;

const RevenueBlock = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY13};
   border-radius: 12px;
   padding: 14px 16px;
   margin-bottom: 24px;
`;

const RevenueHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   flex-wrap: wrap;
   gap: 12px;
   margin-bottom: 12px;
`;

const RevenueTitle = styled.h3`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const RevenueMeta = styled.div`
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 16px;
`;

const RevenueTotals = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
`;

const RevTotal = styled.span<{ $c: string }>`
   font-size: 13px;
   font-weight: 700;
   color: ${({ $c }) => $c};
   display: inline-flex;
   align-items: center;
   gap: 6px;
   &::before {
      content: '';
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: ${({ $c }) => $c};
   }
`;
